import {
  Box,
  Card,
  Checkbox,
  defaultTheme,
  Link,
  NewCarousel as Carousel,
} from "@bca/ui-library";
import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { BorderProps, border } from "styled-system";
import { useInView } from "react-intersection-observer";

import PopoverTablet from "../print/PopoverTablet";
import Gradient from "./Gradient";
import { VehicleResultCardProps } from "./VehicleResultCard.type";
import getEnvironment from "../getEnvironment";
import getCMSDomain from "./getCMSDomain";
import getBackgroundColor from "./getBackgroundColor";
import { IVehicleDetailsModule } from "../common/analytics/analytics.types";
import analytics from "../common/analytics/analytics";
import { IProductPushable } from "../common/analytics/analytics.types";
import ScrollTo from "../utils/ScrollTo";
import extractSelectedImages from "../utils/extractSelectedImages";
import VehicleLabels from "./components/VehicleLabels";
import LinkToDetailsPage, {
  itemCleanedLink,
} from "./components/LinkToDetailsPage";
import VehicleNotes from "./components/VehicleNotes";
import pushCarouselAnalytics from "../analytics/pushCarouselAnalytics";

const ShadowedCard = styled(Card)<BorderProps>`
  ${border}

  box-shadow: ${defaultTheme.shadows.default};
  :hover {
    box-shadow: ${defaultTheme.shadows.hover};
  }
  margin-left: 0;
  overflow: visible;
`;

const GreyBox = styled(Box)`
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const PositionedBox = styled(Box)<{ top: string }>`
  position: absolute;
  top: ${(props) => props.top};
`;

const StyledLink = styled(Link)`
  font-size: 20px;
  font-weight: 500;
`;

const StyledImage = styled.img`
  z-index: -3;
  width: inherit;
  height: inherit;
  object-fit: cover;
`;

const StyledBox = styled(Box)`
  height: auto;
  padding-left: 20px;
  padding-top: 20px;
  z-index: 0;
  position: relative;
`;

const pushSelectAnalytics = (
  vehicleAnalytics: IVehicleDetailsModule,
  action: "select" | "unselect"
) => {
  analytics.push<IProductPushable>({
    event: {
      name: "click",
      category: "button",
      label: "vehicle-select",
      action,
    },
    product: {
      vehicle: [vehicleAnalytics],
    },
  });
};

const VehicleResultCardTablet: React.FunctionComponent<
  VehicleResultCardProps
> = ({
  images,
  exportId,
  vrmNumber,
  lotNumber,
  primaryDescription,
  keyDecisionInfoSlot,
  badgesSlot,
  saleInfoSlot,
  secondaryInfoSlot,
  trackBtnSlot,
  pricingInfoSlot,
  printUrls,
  userNote,
  isTracked,
  vehicleAnalytics,
  selectedVehicles,
  pdfEnabled,
  setSelectedVehicles,
  imageCarouselIndexes,
}) => {
  const [backgroundColor, setBackgroundColor] = useState("");

  const [isCarouselAnalyticsEnabled, setIsCarouselAnalyticsEnabled] =
    useState<boolean>(false);
  const CMSdomainURL = getCMSDomain(getEnvironment());
  const [ref, inView] = useInView({
    triggerOnce: true, // once the image is loaded we should not execute the code anymore
    rootMargin: "200px 0px", // amount of pixels by which the image is under the viewport before it's fetched
  });

  //**  Scroll to Card behaviour

  let queryParamCardItem: string = "";
  let deepLinkScrollFinished: boolean = false;

  if (!deepLinkScrollFinished) {
    const urlParams = new URLSearchParams(window.location.search);
    queryParamCardItem = urlParams?.get("vrm");
  }

  const handleScrollEnd = () => {
    deepLinkScrollFinished = true;
  };

  const getIsQuerySaleMatch = (vrm: string) => {
    return vrm === queryParamCardItem;
  };

  const getIsDeepLinking = (vrm: string) => {
    return !deepLinkScrollFinished && getIsQuerySaleMatch(vrm);
  };

  function removeWhiteSpace(vrm: string) {
    return vrm.replace(/\s+/g, "");
  }

  const getSelectedStatus = useCallback(() => {
    return Boolean(selectedVehicles.find((vrm) => vrm === exportId));
  }, [selectedVehicles, exportId]);

  const addToSelection = () => {
    setSelectedVehicles([...selectedVehicles, exportId]);
  };

  const removeFromSelection = () => {
    setSelectedVehicles((selectedVehicles) => {
      return selectedVehicles?.filter((vrm) => vrm !== exportId);
    });
  };

  useEffect(() => {
    setBackgroundColor(getBackgroundColor(getSelectedStatus(), isTracked));
  }, [isTracked, getSelectedStatus]);

  return (
    <ScrollTo
      id={removeWhiteSpace(vrmNumber)}
      behavior="smooth"
      onScrollEnd={handleScrollEnd}
      active={getIsDeepLinking(removeWhiteSpace(vrmNumber))}
      key={vrmNumber}
    >
      <ShadowedCard
        width="100%"
        height="100%"
        paddingX="0"
        paddingY="0"
        marginY={0}
        border={
          getSelectedStatus() &&
          `1px solid ${defaultTheme.colors["msg-blue-border"]}`
        }
        css={
          getSelectedStatus() &&
          `
          :hover {
            box-shadow: 0px 3px 25px ${defaultTheme.colors["msg-blue-border"]};
          }
        `
        }
      >
        <LinkToDetailsPage
          CMSdomainURL={CMSdomainURL}
          vrmNumber={vrmNumber}
          dataTestId="card-link-tablet"
        />
        <Box width="auto" height="100%" display="flex" flexDirection="column">
          <Box display="flex" flexDirection="row" height="auto">
            <StyledBox>
              <LinkToDetailsPage
                CMSdomainURL={CMSdomainURL}
                vrmNumber={vrmNumber}
                dataTestId="card-link-tablet"
              />
              <PositionedBox
                top="110px"
                display="flex"
                flexDirection="row"
                px={3}
                css={`
                  z-index: 3;
                `}
              >
                <VehicleLabels
                  vrmNumber={vrmNumber}
                  lotNumber={lotNumber}
                  dataTestId="lotNumberTablet"
                />
              </PositionedBox>
              {images.length === 1 ? (
                <Gradient
                  childImage={
                    <StyledImage src={images[0]?.url} alt={images[0]?.alt} />
                  }
                  width="211px"
                  height="120px"
                />
              ) : (
                <Carousel
                  onLoad={() => {
                    setIsCarouselAnalyticsEnabled(true);
                  }}
                  onPageChange={(page) => {
                    pushCarouselAnalytics(
                      vehicleAnalytics,
                      page,
                      isCarouselAnalyticsEnabled
                    );
                  }}
                  width="211px"
                  height="120px"
                  borderRadius="xs"
                  paginationVariant="scrollbar"
                >
                  {extractSelectedImages({
                    images,
                    indexes: imageCarouselIndexes,
                  }).map((image, index) => (
                    <StyledImage
                      src={image?.url}
                      alt={image?.alt}
                      key={index}
                      width="211px"
                      height="120px"
                    />
                  ))}
                </Carousel>
              )}
            </StyledBox>
            <Box
              height="auto"
              width="calc(100% - 211px)"
              padding="20px 20px 0 20px"
              display="flex"
              flexDirection="column"
            >
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box width="auto" height="auto" mb="3px">
                  {badgesSlot}
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  css={`
                    z-index: 1;
                  `}
                >
                  {trackBtnSlot}
                  {printUrls && printUrls?.length > 0 && (
                    <PopoverTablet
                      printUrls={printUrls}
                      vehicleAnalytics={vehicleAnalytics}
                      pdfEnabled={pdfEnabled}
                    />
                  )}
                  <Box
                    p={0}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    alignSelf="flex-start"
                  >
                    <Checkbox
                      id="result-card-checkbox"
                      checked={getSelectedStatus()}
                      onChange={() => {
                        pushSelectAnalytics(
                          vehicleAnalytics,
                          getSelectedStatus() ? "unselect" : "select"
                        );
                        if (getSelectedStatus() === true) {
                          removeFromSelection();
                          return;
                        }
                        addToSelection();
                      }}
                      css={`
                        z-index: 0;
                      `}
                    ></Checkbox>
                  </Box>
                </Box>
              </Box>
              <Box width="auto" height="auto" paddingY="0px">
                {saleInfoSlot}
              </Box>
              <StyledLink href={itemCleanedLink(CMSdomainURL, vrmNumber)}>
                {primaryDescription}
              </StyledLink>
            </Box>
          </Box>

          <Box width="auto" height="auto" padding="20px">
            {secondaryInfoSlot}
          </Box>
          <Box width="auto" height="auto" paddingX="20px" paddingBottom="20px">
            {keyDecisionInfoSlot}
          </Box>
          {Boolean(userNote) && <VehicleNotes userNote={userNote} />}
          <GreyBox
            width="auto"
            height="auto"
            backgroundColor={backgroundColor}
            display="flex"
            justifyContent="space-between"
            p={5}
          >
            <Box width="100%">{pricingInfoSlot}</Box>
          </GreyBox>
        </Box>
      </ShadowedCard>
    </ScrollTo>
  );
};

export default VehicleResultCardTablet;
